import { useState, useEffect, useRef } from 'react'
import { Plus, Trash, FloppyDisk, CaretDown, CaretUp, FileArrowDown, Spinner } from '@phosphor-icons/react'
import { supabase } from '../lib/supabase'

// ── Estilos base ──────────────────────────────────────────────────────────────
const C = {
  bg: '#F5F0EA', card: '#FFFFFF', border: '#DDD5C8',
  dark: '#3B2F2F', brown: '#5C4A3A', muted: '#7A6A5A',
  olive: '#8D9A2E', coral: '#D4614A', amber: '#C98A2E', cream: '#FAF7F3',
}
const CARD = {
  background: C.card, border: `1px solid ${C.border}`, borderRadius: '8px',
  padding: '1.25rem', boxShadow: '0 1px 4px rgba(59,47,47,0.07)',
}
const INP = {
  padding: '0.35rem 0.6rem', border: `1px solid ${C.border}`, borderRadius: '4px',
  fontSize: '0.8rem', fontFamily: "'DM Sans', sans-serif", color: C.dark,
  background: C.cream, outline: 'none', width: '100%', boxSizing: 'border-box',
}
const INP_RO = {
  ...INP,
  background: '#EEEAE4', color: C.muted, cursor: 'default',
}
const BTN_P = {
  display: 'inline-flex', alignItems: 'center', gap: '0.4rem',
  padding: '0.5rem 1rem', background: C.olive, color: '#fff',
  border: 'none', borderRadius: '5px', cursor: 'pointer',
  fontSize: '0.8rem', fontWeight: '600', fontFamily: "'DM Sans', sans-serif",
}
const BTN_G = {
  display: 'inline-flex', alignItems: 'center', gap: '0.4rem',
  padding: '0.35rem 0.75rem', background: 'transparent', color: C.muted,
  border: `1px solid ${C.border}`, borderRadius: '5px', cursor: 'pointer',
  fontSize: '0.78rem', fontFamily: "'DM Sans', sans-serif",
}
const BTN_DANGER = {
  display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
  padding: '0.35rem 0.5rem', background: 'transparent', color: C.coral,
  border: `1px solid ${C.coral}22`, borderRadius: '4px', cursor: 'pointer',
  fontSize: '0.82rem',
}
const MONO = { fontFamily: "'IBM Plex Mono', 'Courier New', monospace" }

// ── Presets de refrigerantes ──────────────────────────────────────────────────
const REFRIGERANT_PRESETS = {
  'R-32':   { gwp: 675,  odp: 0,     cargaKgKw: 0.08 },
  'R-410A': { gwp: 2088, odp: 0,     cargaKgKw: 0.12 },
  'R-134a': { gwp: 1430, odp: 0,     cargaKgKw: 0.10 },
  'R-449A': { gwp: 1397, odp: 0,     cargaKgKw: 0.10 },
  'R-407C': { gwp: 1774, odp: 0,     cargaKgKw: 0.11 },
  'R-404A': { gwp: 3922, odp: 0,     cargaKgKw: 0.13 },
  'R-22':   { gwp: 1810, odp: 0.055, cargaKgKw: 0.10 },
  'Otro':   { gwp: null, odp: null,   cargaKgKw: null  },
}

// ── Valores por defecto BREEAM ────────────────────────────────────────────────
const DEFAULT_FACTORS = {
  vidaUtil:     10,
  fugas:         2,
  purga:         0,
  servicio:      2,
  falloC:       10,
  recuperacion: 90,
}

// ── Helpers ───────────────────────────────────────────────────────────────────
let _nextId = 1
function uid() { return _nextId++ }

function newSistema() {
  return {
    id: uid(),
    nombre:      'Sistema de climatización',
    refrigerante: 'R-410A',
    capacidadKW:  '',
    cargaKg:      '',
    gwp:          REFRIGERANT_PRESETS['R-410A'].gwp,
    odp:          REFRIGERANT_PRESETS['R-410A'].odp,
    ...DEFAULT_FACTORS,
    showAdvanced: false,
  }
}

function calcSistema(s) {
  const cap = parseFloat(s.capacidadKW) || 0
  const preset = REFRIGERANT_PRESETS[s.refrigerante]

  // Carga total: si el usuario introdujo kg manualmente, usa ese valor;
  // de lo contrario, calcula desde cargaKgKw del preset
  let cargaTotal
  if (s.cargaKg !== '' && s.cargaKg !== null && s.cargaKg !== undefined) {
    cargaTotal = parseFloat(s.cargaKg) || 0
  } else if (preset && preset.cargaKgKw !== null) {
    cargaTotal = preset.cargaKgKw * cap
  } else {
    cargaTotal = 0
  }

  const fugas      = parseFloat(s.fugas)       || 0
  const purga      = parseFloat(s.purga)       || 0
  const servicio   = parseFloat(s.servicio)    || 0
  const vidaUtil   = parseFloat(s.vidaUtil)    || 0
  const falloC     = parseFloat(s.falloC)      || 0
  const recuperacion = parseFloat(s.recuperacion) || 0
  const gwp        = parseFloat(s.gwp)         || 0

  // Fórmula oficial Calculadora BREEAM ES CONT 1 Ed.02 (BUG-017 fix):
  //   N = carga × vidaUtil × (L1+L2+S1+S2)  ← S2 es ANUAL (% pérdida/año), multiplica por vidaUtil
  //   O = carga × (1 − RefRecEff)            ← pérdida al final de la vida (recarga incompleta)
  // El bug previo trataba S2 como evento único final (carga × S2 × (1-RefRecEff)).
  // El manual define explícitamente S2 = "probabilidad de fallo crítico (% pérdida/año)" → anual.
  const perdidaOp    = cargaTotal * ((fugas + purga + servicio + falloC) / 100) * vidaUtil
  const perdidaFallo = cargaTotal * (1 - recuperacion / 100)
  const perdidaTotal = perdidaOp + perdidaFallo
  const co2e         = perdidaTotal * gwp
  const edcv         = cap > 0 ? co2e / cap : 0

  return { cargaTotal, perdidaOp, perdidaFallo, perdidaTotal, co2e, edcv }
}

function calcTotal(sistemas) {
  let sumCo2e = 0
  let sumCap  = 0
  // Solo cuentan sistemas con capacidad > 0 (ignora filas vacías)
  const sistemasValidos = sistemas.filter(s => (parseFloat(s.capacidadKW) || 0) > 0)
  sistemasValidos.forEach(s => {
    const r = calcSistema(s)
    sumCo2e += r.co2e
    sumCap  += parseFloat(s.capacidadKW) || 0
  })
  const edcvTotal = sumCap > 0 ? sumCo2e / sumCap : 0
  // Sin sistemas válidos NO se otorgan puntos (corrige bug: edcvTotal=0 daba 2 pts espurios)
  const ptBase = sistemasValidos.length === 0
    ? 0
    : (edcvTotal <= 100 ? 2 : edcvTotal <= 1000 ? 1 : 0)
  const allOdpOk = sistemasValidos.length > 0 && sistemasValidos.every(s => {
    const odp = parseFloat(s.odp)
    const gwp = parseFloat(s.gwp)
    return odp === 0 && gwp <= 5
  })
  const ptODP = allOdpOk ? 1 : 0
  const pts   = ptBase + ptODP
  return { edcvTotal, ptBase, ptODP, pts }
}

function fmt(n, dec = 2) {
  if (!isFinite(n) || isNaN(n)) return '—'
  return n.toLocaleString('es-ES', { minimumFractionDigits: dec, maximumFractionDigits: dec })
}

// ── Componente principal ──────────────────────────────────────────────────────
export default function CONT1Calculator({ projectId, onSave }) {
  const storageKey = `cont1_${projectId}`

  const [opcion, setOpcion] = useState(() => {
    try {
      const saved = JSON.parse(localStorage.getItem(`cont1_${projectId}`) || 'null')
      return saved?.opcion || 'B'
    } catch { return 'B' }
  })
  const [sistemas, setSistemas] = useState(() => {
    try {
      const saved = JSON.parse(localStorage.getItem(`cont1_${projectId}`) || 'null')
      return saved?.sistemas?.length ? saved.sistemas : [newSistema()]
    } catch { return [newSistema()] }
  })
  const [importing, setImporting]   = useState(false)
  const [importMsg, setImportMsg]   = useState('')

  const isFirstRender = useRef(true)
  const hasLoadedFromBD = useRef(false)

  // BUG-016 fix: cargar sistemas desde BD (cont1_calculadora dentro de evaluations.justificacion)
  // al montar como source of truth. Patrón análogo a BUG-014 de RSD 1.
  // Si localStorage está vacío o solo tiene 1 sistema en blanco, recupera la calculadora real
  // que pueda haber guardado el usuario (o aplicado por edición directa en BD).
  useEffect(() => {
    if (hasLoadedFromBD.current) return
    supabase.from('evaluations')
      .select('justificacion')
      .eq('project_id', projectId)
      .eq('criteria_code', 'CNT 1')
      .maybeSingle()
      .then(({ data }) => {
        hasLoadedFromBD.current = true
        if (!data?.justificacion) return
        try {
          const parsed = JSON.parse(data.justificacion)
          const calc = parsed?.cont1_calculadora
          if (!calc?.sistemas?.length) return
          // Sobreescribir local si NO tiene datos numéricos (capacidad/carga vacíos en todas las filas).
          // newSistema() crea por defecto nombre='Sistema de climatización' + R-410A + GWP 2088,
          // así que no podemos usar el nombre para detectar "vacío"; se detecta por datos numéricos.
          const sinDatos = sistemas.every(s =>
            !parseFloat(s.capacidadKW || 0) && !parseFloat(s.cargaKg || 0)
          )
          if (sinDatos) {
            setSistemas(calc.sistemas)
            if (calc.opcion) setOpcion(calc.opcion)
          }
        } catch {}
      })
    // Guard hasLoadedFromBD.current garantiza que solo se ejecuta una vez
    // por proyecto. Leer `sistemas` no debe re-disparar el efecto.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [projectId])

  // Persistir en localStorage al cambiar (omite el primer render)
  useEffect(() => {
    if (isFirstRender.current) { isFirstRender.current = false; return }
    try {
      localStorage.setItem(storageKey, JSON.stringify({ opcion, sistemas }))
    } catch (_) {}
  }, [opcion, sistemas, storageKey])

  // ── Importar sistemas desde documentos subidos en CNT 1 ──────────────────────
  async function handleImportFromDocs() {
    setImporting(true)
    setImportMsg('Buscando documentos subidos en CNT 1…')
    try {
      // 1. Encontrar evaluation CNT 1 del proyecto
      const { data: evalData } = await supabase.from('evaluations')
        .select('id').eq('project_id', projectId).eq('criteria_code', 'CNT 1').maybeSingle()
      if (!evalData?.id) { setImportMsg('No hay documentos subidos en CNT 1. Sube primero las memorias de climatización.'); setImporting(false); return }

      // 2. Obtener evidencias (PDFs)
      const { data: evDocs } = await supabase.from('evidences')
        .select('id, file_name, file_path, storage_bucket')
        .eq('evaluation_id', evalData.id)
        .order('id', { ascending: true })
      const pdfs = (evDocs || []).filter(d => d.file_name?.toLowerCase().endsWith('.pdf'))
      if (!pdfs.length) { setImportMsg('No se encontraron PDFs en CNT 1.'); setImporting(false); return }

      // 3. Enviar a breeam-analyzer con prompt de extracción CSV
      const prompt = `Eres un experto en instalaciones de climatización HVAC en España.
Analiza esta memoria/proyecto de climatización y extrae TODOS los sistemas de refrigeración que contienen refrigerante.
Responde ÚNICAMENTE en formato CSV con | como separador. Primera línea: cabecera fija. Resto: una línea por sistema.
CABECERA (cópiala exacta):
nombre|refrigerante|capacidad_kw|carga_kg|gwp|odp
REGLAS:
- nombre: descripción corta del sistema (ej: "Bomba calor exterior A2", "Split local residuos")
- refrigerante: código exacto del refrigerante (ej: R-410A, R-32, R-134a, R-449A). Si hay varios en el mismo sistema, pon el del circuito principal
- capacidad_kw: potencia frigorífica nominal en kW (número decimal). Si no aparece, escribe NULL
- carga_kg: carga total de refrigerante en kg (número decimal). Si no aparece, escribe NULL
- gwp: potencial de calentamiento global del refrigerante. Si no aparece, escribe NULL
- odp: potencial de agotamiento del ozono. Si no aparece, escribe NULL
- NO incluyas sistemas que usan solo agua (fan coils sin refrigerante directo)
- Una línea por unidad exterior o sistema independiente
EJEMPLO:
nombre|refrigerante|capacidad_kw|carga_kg|gwp|odp
Bomba calor Daikin EMRQ16|R-410A|40|NULL|2088|0
Split local residuos|R-449A|NULL|3.5|1397|0`

      const results = []
      for (let i = 0; i < pdfs.length; i++) {
        const doc = pdfs[i]
        setImportMsg(`Analizando ${i + 1}/${pdfs.length}: ${doc.file_name}…`)
        const bucket = doc.storage_bucket || 'evidences'
        const path = doc.file_path || doc.storage_path
        const { data: urlData } = await supabase.storage.from(bucket).createSignedUrl(path, 3600)
        if (!urlData?.signedUrl) continue

        const supabaseUrl = process.env.REACT_APP_SUPABASE_URL
        const anonKey = process.env.REACT_APP_SUPABASE_ANON_KEY?.trim()
        const resp = await fetch(`${supabaseUrl}/functions/v1/breeam-analyzer`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${anonKey}`, 'apikey': anonKey },
          body: JSON.stringify({ file_infos: [{ signed_url: urlData.signedUrl, name: doc.file_name, media_type: 'application/pdf' }], prompt_text: prompt, model: 'claude-sonnet-4-6', max_tokens: 4096 })
        })
        let fnData = {}
        try { fnData = await resp.json() } catch {}
        if (!resp.ok) continue
        const text = fnData?.content?.[0]?.text || ''
        const lines = text.split('\n').map(l => l.trim()).filter(Boolean)
        const dataLines = lines.filter(l => !l.startsWith('nombre|') && l.includes('|'))
        dataLines.forEach(line => {
          const [nombre, refrigerante, capacidad_kw, carga_kg, gwp, odp] = line.split('|').map(v => v.trim())
          const rKey = Object.keys(REFRIGERANT_PRESETS).find(k => k.toLowerCase() === (refrigerante || '').toLowerCase()) || (refrigerante || 'Otro')
          const preset = REFRIGERANT_PRESETS[rKey] || REFRIGERANT_PRESETS['Otro']
          results.push({
            id: uid(),
            nombre: nombre?.replace(/^NULL$/i, '') || 'Sistema',
            refrigerante: rKey,
            capacidadKW: capacidad_kw && !/null/i.test(capacidad_kw) ? capacidad_kw : '',
            cargaKg: carga_kg && !/null/i.test(carga_kg) ? carga_kg : '',
            gwp: gwp && !/null/i.test(gwp) ? parseFloat(gwp) : (preset.gwp ?? ''),
            odp: odp && !/null/i.test(odp) ? parseFloat(odp) : (preset.odp ?? ''),
            ...DEFAULT_FACTORS,
            showAdvanced: false,
          })
        })
      }

      if (results.length === 0) {
        setImportMsg('No se encontraron sistemas de refrigeración en los documentos.')
      } else {
        setSistemas(results)
        setImportMsg(`✅ ${results.length} sistema${results.length > 1 ? 's' : ''} importado${results.length > 1 ? 's' : ''}. Revisa y completa los campos en NULL.`)
      }
    } catch (err) {
      setImportMsg(`Error: ${err.message}`)
    }
    setImporting(false)
  }

  // ── Mutadores ────────────────────────────────────────────────────────────────
  function addSistema() {
    setSistemas(prev => [...prev, newSistema()])
  }

  function deleteSistema(id) {
    setSistemas(prev => prev.filter(s => s.id !== id))
  }

  function updateSistema(id, field, value) {
    setSistemas(prev => prev.map(s => {
      if (s.id !== id) return s
      const updated = { ...s, [field]: value }
      // Si cambia refrigerante, auto-rellenar gwp y odp
      if (field === 'refrigerante') {
        const preset = REFRIGERANT_PRESETS[value]
        if (preset) {
          updated.gwp = preset.gwp !== null ? preset.gwp : ''
          updated.odp = preset.odp !== null ? preset.odp : ''
          // Si la carga estaba vacía y hay cargaKgKw, dejar vacío para auto-calcular
        }
      }
      return updated
    }))
  }

  function toggleAdvanced(id) {
    setSistemas(prev => prev.map(s =>
      s.id === id ? { ...s, showAdvanced: !s.showAdvanced } : s
    ))
  }

  // ── Resultado ────────────────────────────────────────────────────────────────
  const result = opcion === 'A' ? { pts: 3, opcion: 'A' } : calcTotal(sistemas)

  function handleSave() {
    if (opcion === 'A') {
      onSave && onSave({ pts: 3, opcion: 'A', sistemas: [], edcvTotal: 0, ptBase: 2, ptODP: 1 })
    } else {
      const { edcvTotal, ptBase, ptODP, pts } = result
      onSave && onSave({ pts, opcion: 'B', sistemas, edcvTotal, ptBase, ptODP })
    }
  }

  // ── Render ───────────────────────────────────────────────────────────────────
  return (
    <div style={{ fontFamily: "'DM Sans', sans-serif", color: C.dark, fontSize: '0.875rem' }}>

      {/* Cabecera */}
      <div style={{ marginBottom: '1.25rem' }}>
        <h2 style={{ margin: 0, fontSize: '1.1rem', fontWeight: '700', color: C.dark }}>
          Calculadora CNT 1 — Impacto de los refrigerantes
        </h2>
        <p style={{ margin: '0.3rem 0 0', color: C.muted, fontSize: '0.8rem' }}>
          BREEAM ES NC · Contaminación · Máx. 3 puntos
        </p>
      </div>

      {/* Toggle Opción A / B */}
      <div style={{ display: 'flex', gap: '0.5rem', marginBottom: '1.5rem', flexWrap: 'wrap' }}>
        <button
          style={{
            ...BTN_G,
            background: opcion === 'A' ? C.olive : 'white',
            color:      opcion === 'A' ? 'white'  : C.brown,
            borderColor: C.olive,
            fontWeight: opcion === 'A' ? '700' : '500',
          }}
          onClick={() => setOpcion('A')}
        >
          Opción A — Sin refrigerantes (3 pts)
        </button>
        <button
          style={{
            ...BTN_G,
            background: opcion === 'B' ? C.brown : 'white',
            color:      opcion === 'B' ? 'white'  : C.brown,
            borderColor: C.brown,
            fontWeight: opcion === 'B' ? '700' : '500',
          }}
          onClick={() => setOpcion('B')}
        >
          Opción B — Con refrigerantes
        </button>
      </div>

      {/* ── OPCIÓN A ─────────────────────────────────────────────────────────── */}
      {opcion === 'A' && (
        <div style={{ ...CARD, background: '#F0F5E8', borderColor: C.olive, marginBottom: '1.5rem' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem' }}>
            <span style={{ fontSize: '2rem' }}>✅</span>
            <div>
              <div style={{ fontWeight: '700', fontSize: '1rem', color: C.olive }}>
                3 / 3 puntos — Sin refrigerantes
              </div>
              <div style={{ color: C.muted, fontSize: '0.8rem', marginTop: '0.2rem' }}>
                El edificio no utiliza sistemas de refrigeración con gases fluorados.
                Se obtienen los 3 puntos directamente.
              </div>
            </div>
          </div>
        </div>
      )}

      {/* ── OPCIÓN B ─────────────────────────────────────────────────────────── */}
      {opcion === 'B' && (
        <>
          {/* Tabla de sistemas */}
          <div style={{ marginBottom: '1.25rem' }}>
            <div style={{ overflowX: 'auto', borderRadius: '8px', border: `1px solid ${C.border}` }}>
              <table style={{ width: '100%', borderCollapse: 'collapse', minWidth: '900px', background: C.card }}>
                <thead>
                  <tr style={{ background: C.cream, borderBottom: `1px solid ${C.border}` }}>
                    {[
                      'Sistema', 'Refrigerante', 'Capacidad (kW)',
                      'Carga (kg)', 'GWP', 'ODP',
                      'CO₂e (kg)', 'EDCV (kgCO₂e/kW)', 'Factores', '',
                    ].map((h, i) => (
                      <th key={i} style={{
                        padding: '0.55rem 0.75rem', textAlign: 'left',
                        fontSize: '0.75rem', fontWeight: '600', color: C.muted,
                        whiteSpace: 'nowrap',
                      }}>
                        {h}
                      </th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {sistemas.map((s, idx) => {
                    const r = calcSistema(s)
                    return (
                      <>
                        {/* Fila principal */}
                        <tr
                          key={s.id}
                          style={{
                            borderBottom: s.showAdvanced
                              ? 'none'
                              : `1px solid ${C.border}`,
                            background: idx % 2 === 0 ? C.card : '#FDFAF6',
                          }}
                        >
                          {/* Nombre */}
                          <td style={{ padding: '0.45rem 0.75rem', minWidth: '160px' }}>
                            <input
                              style={INP}
                              value={s.nombre}
                              onChange={e => updateSistema(s.id, 'nombre', e.target.value)}
                              placeholder="Nombre del sistema"
                            />
                          </td>

                          {/* Refrigerante */}
                          <td style={{ padding: '0.45rem 0.75rem', minWidth: '120px' }}>
                            <select
                              style={INP}
                              value={s.refrigerante}
                              onChange={e => updateSistema(s.id, 'refrigerante', e.target.value)}
                            >
                              {Object.keys(REFRIGERANT_PRESETS).map(k => (
                                <option key={k} value={k}>{k}</option>
                              ))}
                            </select>
                          </td>

                          {/* Capacidad kW */}
                          <td style={{ padding: '0.45rem 0.75rem', minWidth: '110px' }}>
                            <input
                              style={INP}
                              type="number" min="0" step="0.1"
                              value={s.capacidadKW}
                              onChange={e => updateSistema(s.id, 'capacidadKW', e.target.value)}
                              placeholder="kW"
                            />
                          </td>

                          {/* Carga kg */}
                          <td style={{ padding: '0.45rem 0.75rem', minWidth: '110px' }}>
                            <input
                              style={INP}
                              type="number" min="0" step="0.01"
                              value={s.cargaKg}
                              onChange={e => updateSistema(s.id, 'cargaKg', e.target.value)}
                              placeholder={
                                s.capacidadKW && REFRIGERANT_PRESETS[s.refrigerante]?.cargaKgKw
                                  ? `auto: ${fmt(REFRIGERANT_PRESETS[s.refrigerante].cargaKgKw * (parseFloat(s.capacidadKW) || 0), 2)}`
                                  : 'kg'
                              }
                            />
                          </td>

                          {/* GWP */}
                          <td style={{ padding: '0.45rem 0.75rem', minWidth: '80px' }}>
                            <input
                              style={INP}
                              type="number" min="0"
                              value={s.gwp ?? ''}
                              onChange={e => updateSistema(s.id, 'gwp', e.target.value)}
                              placeholder="GWP"
                            />
                          </td>

                          {/* ODP */}
                          <td style={{ padding: '0.45rem 0.75rem', minWidth: '80px' }}>
                            <input
                              style={INP}
                              type="number" min="0" step="0.001"
                              value={s.odp ?? ''}
                              onChange={e => updateSistema(s.id, 'odp', e.target.value)}
                              placeholder="ODP"
                            />
                          </td>

                          {/* CO2e (calculado) */}
                          <td style={{ padding: '0.45rem 0.75rem', minWidth: '110px' }}>
                            <div style={{ ...INP_RO, ...MONO, textAlign: 'right' }}>
                              {fmt(r.co2e)}
                            </div>
                          </td>

                          {/* EDCV (calculado) */}
                          <td style={{ padding: '0.45rem 0.75rem', minWidth: '130px' }}>
                            <div style={{ ...INP_RO, ...MONO, textAlign: 'right' }}>
                              {fmt(r.edcv)}
                            </div>
                          </td>

                          {/* Toggle avanzados */}
                          <td style={{ padding: '0.45rem 0.5rem', minWidth: '110px' }}>
                            <button
                              style={{ ...BTN_G, fontSize: '0.72rem', gap: '0.25rem', whiteSpace: 'nowrap' }}
                              onClick={() => toggleAdvanced(s.id)}
                            >
                              {s.showAdvanced ? <CaretUp size={12} /> : <CaretDown size={12} />}
                              {s.showAdvanced ? 'Ocultar' : 'Ver factores'}
                            </button>
                          </td>

                          {/* Eliminar */}
                          <td style={{ padding: '0.45rem 0.5rem' }}>
                            <button
                              style={BTN_DANGER}
                              onClick={() => deleteSistema(s.id)}
                              title="Eliminar sistema"
                              disabled={sistemas.length === 1}
                            >
                              <Trash size={15} />
                            </button>
                          </td>
                        </tr>

                        {/* Fila de factores avanzados */}
                        {s.showAdvanced && (
                          <tr
                            key={`adv-${s.id}`}
                            style={{
                              background: '#F8F5F0',
                              borderBottom: `1px solid ${C.border}`,
                            }}
                          >
                            <td colSpan={10} style={{ padding: '0.75rem 1rem' }}>
                              <div style={{
                                display: 'grid',
                                gridTemplateColumns: 'repeat(auto-fill, minmax(160px, 1fr))',
                                gap: '0.75rem',
                              }}>
                                {[
                                  { field: 'vidaUtil',     label: 'Vida útil (años)',       step: '1' },
                                  { field: 'fugas',        label: 'Fugas (%/año)',           step: '0.1' },
                                  { field: 'purga',        label: 'Purga (%/año)',           step: '0.1' },
                                  { field: 'servicio',     label: 'Servicio (%/año)',        step: '0.1' },
                                  { field: 'falloC',       label: 'Fallo crítico (%)',       step: '0.1' },
                                  { field: 'recuperacion', label: 'Recuperación (%)',        step: '0.1' },
                                ].map(({ field, label, step }) => (
                                  <label key={field} style={{ display: 'flex', flexDirection: 'column', gap: '0.25rem' }}>
                                    <span style={{ fontSize: '0.72rem', color: C.muted, fontWeight: '600' }}>
                                      {label}
                                    </span>
                                    <input
                                      style={INP}
                                      type="number" min="0" step={step}
                                      value={s[field]}
                                      onChange={e => updateSistema(s.id, field, e.target.value)}
                                    />
                                  </label>
                                ))}
                                {/* Desglose calculado */}
                                <div style={{ display: 'flex', flexDirection: 'column', gap: '0.25rem', gridColumn: 'span 2' }}>
                                  <span style={{ fontSize: '0.72rem', color: C.muted, fontWeight: '600' }}>
                                    Desglose de pérdidas
                                  </span>
                                  <div style={{
                                    background: C.cream, border: `1px solid ${C.border}`,
                                    borderRadius: '4px', padding: '0.5rem 0.75rem',
                                    fontSize: '0.75rem', lineHeight: '1.6',
                                    ...MONO,
                                  }}>
                                    <div>Carga total: <strong>{fmt(r.cargaTotal, 3)} kg</strong></div>
                                    <div>Pérdida operacional: <strong>{fmt(r.perdidaOp, 3)} kg</strong></div>
                                    <div>Pérdida por fallo: <strong>{fmt(r.perdidaFallo, 3)} kg</strong></div>
                                    <div>Pérdida total: <strong>{fmt(r.perdidaTotal, 3)} kg</strong></div>
                                  </div>
                                </div>
                              </div>
                            </td>
                          </tr>
                        )}
                      </>
                    )
                  })}
                </tbody>
              </table>
            </div>

            {/* Añadir sistema + Importar desde docs */}
            <div style={{ marginTop: '0.75rem', display: 'flex', gap: '0.5rem', alignItems: 'center', flexWrap: 'wrap' }}>
              <button style={BTN_G} onClick={addSistema}>
                <Plus size={14} /> Añadir sistema
              </button>
              <button style={{ ...BTN_G, color: C.amber, borderColor: C.amber }} onClick={handleImportFromDocs} disabled={importing}>
                {importing ? <Spinner size={14} style={{ animation: 'spin 1s linear infinite' }} /> : <FileArrowDown size={14} />}
                {importing ? 'Importando…' : 'Importar desde documentos CNT 1'}
              </button>
              {importMsg && (
                <span style={{ fontSize: '0.78rem', color: importMsg.startsWith('✅') ? C.olive : C.coral, fontStyle: 'italic' }}>
                  {importMsg}
                </span>
              )}
            </div>
          </div>

          {/* Tarjeta de resultados */}
          <ResultCard result={result} />
        </>
      )}

      {/* Botón de acción: guardar puntuación. El botón CAL CONT1 vive en la fila estándar del criterio (App.js). */}
      <div style={{ marginTop: '1.5rem', display: 'flex', gap: '0.5rem', flexWrap: 'wrap' }}>
        <button style={BTN_P} onClick={handleSave}>
          <FloppyDisk size={16} />
          Guardar puntuación
        </button>
      </div>
    </div>
  )
}

// El botón CAL CONT1 (descarga de la Calculadora oficial rellena) vive ahora
// en App.js, en la fila estándar de botones del criterio, junto a CAL AG1,
// CAL MAT3, CAL MAT1 y CAL SyB2.

// ── Tarjeta de resultados ─────────────────────────────────────────────────────
function ResultCard({ result }) {
  const { edcvTotal, ptBase, ptODP, pts } = result
  const ptsColor = pts >= 2 ? C.olive : pts === 1 ? C.amber : C.coral

  const edcvLabel = edcvTotal <= 100
    ? `≤ 100 → 2 puntos base`
    : edcvTotal <= 1000
    ? `≤ 1 000 → 1 punto base`
    : `> 1 000 → 0 puntos base`

  return (
    <div style={{ ...CARD, borderLeft: `4px solid ${ptsColor}`, marginBottom: '0.5rem' }}>
      <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', flexWrap: 'wrap', gap: '1rem' }}>

        {/* Métricas */}
        <div style={{ display: 'flex', flexDirection: 'column', gap: '0.6rem', minWidth: '260px' }}>
          <div>
            <span style={{ fontSize: '0.72rem', color: C.muted, fontWeight: '600', textTransform: 'uppercase', letterSpacing: '0.04em' }}>
              EDCV total
            </span>
            <div style={{ ...MONO, fontSize: '1.4rem', fontWeight: '700', color: C.dark, lineHeight: '1.2', marginTop: '0.1rem' }}>
              {fmt(edcvTotal)} <span style={{ fontSize: '0.8rem', fontWeight: '400', color: C.muted }}>kgCO₂e/kW</span>
            </div>
            <div style={{ fontSize: '0.75rem', color: C.muted, marginTop: '0.1rem' }}>
              {edcvLabel}
            </div>
          </div>

          <div style={{ display: 'flex', gap: '1rem', flexWrap: 'wrap' }}>
            {/* Pt base */}
            <div style={{
              background: ptBase > 0 ? '#F0F5E8' : '#FDF0EE',
              border: `1px solid ${ptBase > 0 ? C.olive : C.coral}44`,
              borderRadius: '6px', padding: '0.45rem 0.75rem',
            }}>
              <div style={{ fontSize: '0.7rem', color: C.muted, fontWeight: '600' }}>Puntos base</div>
              <div style={{ ...MONO, fontSize: '1.2rem', fontWeight: '700', color: ptBase > 0 ? C.olive : C.coral }}>
                {ptBase} <span style={{ fontSize: '0.75rem', fontWeight: '400', color: C.muted }}>/ 2</span>
              </div>
            </div>

            {/* Pt ODP */}
            <div style={{
              background: ptODP === 1 ? '#F0F5E8' : '#FDF0EE',
              border: `1px solid ${ptODP === 1 ? C.olive : C.coral}44`,
              borderRadius: '6px', padding: '0.45rem 0.75rem',
            }}>
              <div style={{ fontSize: '0.7rem', color: C.muted, fontWeight: '600' }}>Punto ODP/GWP≤5</div>
              <div style={{ ...MONO, fontSize: '1.2rem', fontWeight: '700', color: ptODP === 1 ? C.olive : C.coral }}>
                {ptODP} <span style={{ fontSize: '0.75rem', fontWeight: '400', color: C.muted }}>/ 1</span>
              </div>
              {ptODP === 0 && (
                <div style={{ fontSize: '0.68rem', color: C.coral, marginTop: '0.15rem' }}>
                  Requiere ODP=0 y GWP≤5 en todos los sistemas
                </div>
              )}
            </div>
          </div>
        </div>

        {/* Total */}
        <div style={{
          background: ptsColor + '14',
          border: `2px solid ${ptsColor}55`,
          borderRadius: '10px', padding: '1rem 1.5rem',
          display: 'flex', flexDirection: 'column', alignItems: 'center',
          minWidth: '120px',
        }}>
          <div style={{ fontSize: '0.72rem', color: C.muted, fontWeight: '600', textTransform: 'uppercase', letterSpacing: '0.06em' }}>
            TOTAL
          </div>
          <div style={{ ...MONO, fontSize: '2.6rem', fontWeight: '800', color: ptsColor, lineHeight: '1.1' }}>
            {pts}
          </div>
          <div style={{ fontSize: '0.78rem', color: C.muted }}>/ 3 pts</div>
          <div style={{
            marginTop: '0.4rem', fontSize: '0.7rem', fontWeight: '700',
            color: 'white', background: ptsColor, borderRadius: '4px',
            padding: '0.15rem 0.5rem',
          }}>
            {pts === 3 ? 'EJEMPLAR' : pts >= 2 ? 'BUENO' : pts === 1 ? 'ACEPTABLE' : 'INSUFICIENTE'}
          </div>
        </div>
      </div>
    </div>
  )
}
